import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { Package, Menu, X, User, LogOut, LayoutDashboard, Settings, CreditCard, Building2, Ship } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isLanding = location.pathname === '/';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${isLanding ? 'bg-slate-900' : 'bg-white border-b border-slate-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <span className={`font-heading font-bold text-xl ${isLanding ? 'text-white' : 'text-slate-900'}`}>
              ISMParcel
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/tracking" 
              className={`font-medium transition-colors ${isLanding ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Suivre un colis
            </Link>
            <Link 
              to="/rates" 
              className={`font-medium transition-colors ${isLanding ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Tarifs
            </Link>
            <Link 
              to="/fret" 
              className={`flex items-center gap-1.5 font-medium transition-colors ${isLanding ? 'text-amber-300 hover:text-amber-200' : 'text-amber-600 hover:text-amber-700'}`}
              data-testid="nav-fret-link"
            >
              <Ship className="w-4 h-4" />
              Fret
            </Link>
            <Link 
              to="/pro" 
              className={`flex items-center gap-1.5 font-medium transition-colors ${isLanding ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
            >
              <Building2 className="w-4 h-4" />
              Pro
            </Link>
            <Link 
              to="/abonnements" 
              className={`flex items-center gap-1.5 font-medium transition-colors ${isLanding ? 'text-emerald-300 hover:text-emerald-200' : 'text-emerald-600 hover:text-emerald-700'}`}
              data-testid="nav-subscription-link"
            >
              ⭐ Abonnements
            </Link>
            <Link 
              to="/deals" 
              className={`flex items-center gap-1.5 font-medium transition-colors ${isLanding ? 'text-amber-300 hover:text-amber-200' : 'text-amber-600 hover:text-amber-700'}`}
              data-testid="nav-deals-link"
            >
              🔥 ISM Deals
            </Link>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant={isLanding ? "outline" : "default"}
                    className={isLanding ? "border-white text-white hover:bg-white hover:text-slate-900" : "bg-blue-600 hover:bg-blue-700"}
                    data-testid="user-menu-btn"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {user.first_name}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.first_name} {user.last_name}</p>
                    <p className="text-xs text-muted-foreground">{user.customer_number}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="dashboard-link">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Mon espace
                  </DropdownMenuItem>
                  {!user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Paramètres & Paiement
                    </DropdownMenuItem>
                  )}
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link">
                      <Settings className="w-4 h-4 mr-2" />
                      Administration
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className={isLanding ? "text-white hover:bg-white/10" : ""}
                    data-testid="login-btn"
                  >
                    Connexion
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="register-btn"
                  >
                    S'inscrire
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className={`w-6 h-6 ${isLanding ? 'text-white' : 'text-slate-900'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isLanding ? 'text-white' : 'text-slate-900'}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className={`md:hidden ${isLanding ? 'bg-slate-900' : 'bg-white'} border-t ${isLanding ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className="px-4 py-4 space-y-3">
            <Link 
              to="/tracking" 
              className={`block py-2 font-medium ${isLanding ? 'text-white' : 'text-slate-900'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Suivre un colis
            </Link>
            <Link 
              to="/rates" 
              className={`block py-2 font-medium ${isLanding ? 'text-white' : 'text-slate-900'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Tarifs
            </Link>
            <Link 
              to="/fret" 
              className={`flex items-center gap-2 py-2 font-medium ${isLanding ? 'text-amber-300' : 'text-amber-600'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Ship className="w-4 h-4" />
              Fret & Logistique
            </Link>
            <Link 
              to="/pro" 
              className={`flex items-center gap-2 py-2 font-medium ${isLanding ? 'text-blue-400' : 'text-blue-600'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Building2 className="w-4 h-4" />
              Offre Pro
            </Link>
            <Link 
              to="/abonnements" 
              className={`flex items-center gap-2 py-2 font-medium ${isLanding ? 'text-emerald-300' : 'text-emerald-600'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              ⭐ Abonnements
            </Link>
            <Link 
              to="/deals" 
              className={`flex items-center gap-2 py-2 font-medium ${isLanding ? 'text-amber-300' : 'text-amber-600'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              🔥 ISM Deals
            </Link>
            
            {user ? (
              <>
                <Link 
                  to="/dashboard" 
                  className={`block py-2 font-medium ${isLanding ? 'text-white' : 'text-slate-900'}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Mon espace
                </Link>
                {!user.is_admin && (
                  <Link 
                    to="/settings" 
                    className={`block py-2 font-medium ${isLanding ? 'text-white' : 'text-slate-900'}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Paramètres & Paiement
                  </Link>
                )}
                {user.is_admin && (
                  <Link 
                    to="/admin" 
                    className={`block py-2 font-medium ${isLanding ? 'text-white' : 'text-slate-900'}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Administration
                  </Link>
                )}
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="block py-2 font-medium text-red-500"
                >
                  Déconnexion
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-2 pt-2">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full">Connexion</Button>
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">S'inscrire</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
